import { BookOutlined, DeleteOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Drawer, Tag, message } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';
import { uploadFileFromObject } from '../../api/common/image-upload.js';
import { getItemCustomerArtworks } from '../../api/project-item/project-item.js';
import { CompanyArtworkPicker } from '../data/customer-artwork/company-artwork-library.jsx';
import { getFilePathForView, getPreviewUrl } from '../../library/utilities/urls.js';
import { formatDateTime } from '../../library/utilities/intl.js';
import { AddFileUpload } from '../../ui/addFileUpload.jsx';
import styles from './file-manager.module.scss';
import Confirm from '../message/Confirm.jsx';

const FileManager = ({ title = "", projectId, files = [], acceptedFileTypes = null, isOpen = false, loading, deleteOwnOnly, onUpload, onDelete, onClose, libraryItemId, onLibraryChange, ...otherProps }) => {
    // const [isDeleting, setIsDeleting] = useState(null)
    const { user } = useRouteLoaderData('Root')
    const [isUploading, setIsUploading] = useState(loading)
    const fileUploaderRef = useRef([])
    const [isLibraryOpen, setIsLibraryOpen] = useState(false)
    const [hasLibraryArtwork, setHasLibraryArtwork] = useState(false)

    // Show "Add from company library" only when the company has artwork; re-checked after uploads
    useEffect(() => {
        if (!libraryItemId || !isOpen) return
        getItemCustomerArtworks(libraryItemId)
            .then((artworks) => setHasLibraryArtwork(artworks.length > 0))
            .catch(() => setHasLibraryArtwork(false))
    }, [libraryItemId, isOpen, files])

    const handleFileUpload = async () => {
        if (fileUploaderRef.current.length === 0) {
            alert('Please select atleast one file')
            return
        }
        setIsUploading(true)
        try {
            const fileIds = await Promise.all(fileUploaderRef.current.map(({ file }) => uploadFileFromObject(file)))
            const imageData = fileIds.map((id, index) => ({
                id,
                comment: fileUploaderRef.current[index].comment
            }))
            // onUpload may return false to say saving failed: keep the selection for a retry
            const saved = await onUpload(imageData)
            if (saved !== false) fileUploaderRef.current = []
        }
        catch (error) {
            message.error(error?.message || 'Unable to upload files')
        }
        finally {
            setIsUploading(loading)
        }
    }

    // const handleDeleteFile = async (url) => {
    //     setIsDeleting(url)
    //     try {
    //         const msg = await deleteImage(projectId, url)
    //         message.success(msg)
    //         revalidator.revalidate()
    //     }
    //     catch (error) {
    //         message.error(error.message)
    //     }
    //     setIsDeleting(null)
    // }

    return (
        <Drawer title={title} open={isOpen} onClose={onClose} size='large' footer={
            <div className="actions align--right">
                {(!!libraryItemId && hasLibraryArtwork) && <Button onClick={() => setIsLibraryOpen(true)} icon={<BookOutlined />} disabled={isUploading}>Add from company library</Button>}
                <Button onClick={() => handleFileUpload()} icon={<UploadOutlined />} loading={isUploading} type="primary">Upload</Button>
                <Button onClick={onClose} disabled={isUploading} danger>Close</Button>
            </div>
        } {...otherProps}>
            {!!files && files.length > 0 && (
                <div className={`${styles.files} ${(loading || isUploading) ? 'loading' : ''}`}>
                    {files.map(file => (
                        <div className={styles.file} key={file.url}>
                            <div className={styles.filePreview}>
                                <img src={getPreviewUrl(file.url, projectId)} />
                            </div>
                            <div className={styles.fileComment}>
                                <p className={styles.label}>
                                    <span>{file.name}</span>
                                    {!!file.customer_artwork_id && <Tag className={styles.libraryTag} color="blue" bordered={false} icon={<BookOutlined />}>Company library</Tag>}
                                </p>
                                {!!file.uploaded_time && <p style={{ color: '#8c8c8c', fontSize: '0.8rem', margin: 0 }}>{formatDateTime(file.uploaded_time)}</p>}
                                {!!file.comment && <p className={styles.comment}>{file.comment}</p>}
                            </div>
                            <div className={`actions align--left ${styles.fileActions}`}>
                                {(onDelete && (!deleteOwnOnly || file.user_id === user.id)) && <Confirm title="Delete file" content='Selected file will be deleted permanently' okText='Delete' onConfirm={() => { onDelete(file.url) }} icon={<DeleteOutlined />} >Delete</Confirm>}
                                <Button href={getFilePathForView(file.url, projectId)} icon={<EyeOutlined />} target='_blank'>View</Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <AddFileUpload ref={fileUploaderRef} acceptedFileTypes={acceptedFileTypes} />
            {!!libraryItemId && <CompanyArtworkPicker itemId={libraryItemId} open={isLibraryOpen} onClose={() => setIsLibraryOpen(false)} onLinked={onLibraryChange} />}
        </Drawer>
    )
}
FileManager.propTypes = {
    title: PropTypes.string,
    projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    files: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        comment: PropTypes.string,
    })),
    acceptedFileTypes: PropTypes.arrayOf(PropTypes.string),
    isOpen: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    deleteOwnOnly: PropTypes.bool,
    onUpload: PropTypes.func.isRequired,
    onDelete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    libraryItemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onLibraryChange: PropTypes.func,
}

export default FileManager
